import { SimpleGrid, Stack } from "@mantine/core";
import { SimpleBreadcrumbs } from "../../../components/SimpleBreadcrumbs";
import { useIntl } from "react-intl";
import { InputTokensUsageChart, OutputTokensUsageChart } from "../components/AITokensUsageChart.js";
import { MapLoadsUsageChart } from "../components/MapLoadsUsageChart.js";
import { ProcessingUsageChart } from "../components/ProcessingUsageChart.js";
import { StorageUsageChart } from "../components/StorageUsageChart.js";
import { TilesRequestsUsageChart } from "../components/TilesRequestsUsageChart.js";
import { type ReactElement, useMemo } from "react";
import { TIERED_PRODUCT_IDS, TieredPriceProductId } from "@project/common";
import { UsageControls } from "./UsageControls.js";
import { CustomStylesUsageChart } from "../components/CustomStylesUsageChart.js";
import { CustomTilesetsUsageChart } from "../components/CustomTilesetsUsageChart.js";
import { useUsageControlsProps } from "./hooks/propGenerators.js";
import { TeamSeatsUsageChart } from "../components/TeamSeatsUsageChart.js";

export function Usage() {
  const intl = useIntl();
  const usageControlsProps = useUsageControlsProps();
  const {
    dateRange: [from, to],
    timeInterval: interval,
    apiKeyId,
    addToDetailedView,
    metricsInDetailedView,
  } = usageControlsProps;

  const productIdToComponent: Partial<Record<TieredPriceProductId, ReactElement>> = useMemo(() => ({
    [TieredPriceProductId.TieredTileUsage]: (
      <TilesRequestsUsageChart
        key={TieredPriceProductId.TieredTileUsage}
        from={from}
        to={to}
        interval={interval}
        apiKeyId={apiKeyId}
        addToDetailedView={() => {
          addToDetailedView(TieredPriceProductId.TieredTileUsage);
        }}
      />
    ),
    [TieredPriceProductId.TieredMapSession]: (
      <MapLoadsUsageChart
        key={TieredPriceProductId.TieredMapSession}
        from={from}
        to={to}
        interval={interval}
        apiKeyId={apiKeyId}
        addToDetailedView={() => {
          addToDetailedView(TieredPriceProductId.TieredMapSession);
        }}
      />
    ),
    [TieredPriceProductId.TieredInputToken]: (
      <InputTokensUsageChart
        key={TieredPriceProductId.TieredInputToken}
        from={from}
        to={to}
        interval={interval}
        addToDetailedView={() => {
          addToDetailedView(TieredPriceProductId.TieredInputToken);
        }}
      />
    ),
    [TieredPriceProductId.TieredOutputToken]: (
      <OutputTokensUsageChart
        key={TieredPriceProductId.TieredOutputToken}
        from={from}
        to={to}
        interval={interval}
        addToDetailedView={() => {
          addToDetailedView(TieredPriceProductId.TieredOutputToken);
        }}
      />
    ),
    [TieredPriceProductId.TieredProcessing]: (
      <ProcessingUsageChart
        key={TieredPriceProductId.TieredProcessing}
        from={from}
        to={to}
        interval={interval}
        apiKeyId={apiKeyId}
        addToDetailedView={() => {
          addToDetailedView(TieredPriceProductId.TieredProcessing);
        }}
      />
    ),
    [TieredPriceProductId.TieredStorage]: (
      <StorageUsageChart
        key={TieredPriceProductId.TieredStorage}
        from={from}
        to={to}
        interval={interval}
        addToDetailedView={() => {
          addToDetailedView(TieredPriceProductId.TieredStorage);
        }}
      />
    ),
    [TieredPriceProductId.TieredCustomStyle]: (
      <CustomStylesUsageChart
        key={TieredPriceProductId.TieredCustomStyle}
        from={from}
        to={to}
        interval={interval}
        addToDetailedView={() => {
          addToDetailedView(TieredPriceProductId.TieredCustomStyle);
        }}
      />
    ),
    [TieredPriceProductId.TieredCustomTileset]: (
      <CustomTilesetsUsageChart
        key={TieredPriceProductId.TieredCustomTileset}
        from={from}
        to={to}
        interval={interval}
        addToDetailedView={() => {
          addToDetailedView(TieredPriceProductId.TieredCustomTileset);
        }}
      />
    ),
    [TieredPriceProductId.TieredAccountUser]: (
      <TeamSeatsUsageChart
        key={TieredPriceProductId.TieredAccountUser}
        from={from}
        to={to}
        interval={interval}
        addToDetailedView={() => {
          addToDetailedView(TieredPriceProductId.TieredAccountUser);
        }}
      />
    ),
  }), [from, to, interval, apiKeyId, addToDetailedView]);

  const metrics: TieredPriceProductId[] = metricsInDetailedView.length > 0
    ? metricsInDetailedView
    : TIERED_PRODUCT_IDS;

  return (
    <Stack gap="lg" h="100%">
      {!metricsInDetailedView.length ?
          (
            <SimpleBreadcrumbs
              current={intl.formatMessage({
                defaultMessage: "Usage",
                id: "navigation.usage",
              })}
              description={intl.formatMessage({
                defaultMessage: "Usage patterns and trends across all metrics.",
                id: "text.usageDescription",
              })}
            />
          )
        :
          (
            <SimpleBreadcrumbs
              current={intl.formatMessage({
                defaultMessage: "Detailed view",
                id: "navigation.detailedView",
              })}
              description={intl.formatMessage({
                defaultMessage: "Detailed usage for the specified metric",
                id: "text.detailedViewDescription",
              })}
            />
          )}

      <UsageControls {...usageControlsProps} />

      <SimpleGrid
        cols={{ base: 1, md: 2, lg: 3 }}
        spacing="lg"
        verticalSpacing="lg"
        pb="xl"
      >
        {metrics.map(metric => productIdToComponent[metric])}
      </SimpleGrid>
    </Stack>
  );
}
